import {
  Option,
  SelectBox,
  SelectBoxProps,
  styled,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { Location } from "../locations.types";
import { LocationsQuery } from "../locations.query";
import { GetLocationsQuery } from "../locations.api";

export type LocationSelectorProps = Omit<
  SelectBoxProps,
  | "data"
  | "renderOption"
  | "OptionTooltip"
  | "searchable"
  | "loading"
  | "defaultValue"
  | "value"
> & {
  defaultValue?: Option;
  value?: Option;
  query?: GetLocationsQuery;
};

// Tooltip content component
const TooltipContent = styled(
  ({ className, data }: { className?: string; data: Location }) => {
    return (
      <div className={className}>
        <div className="detail-title">Location Details</div>
        <div className="detail-item">
          <div className="detail-label">Type: </div>
          <div className="detail-value">{data.location_type || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Office: </div>
          <div className="detail-value">{data.office_name || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Group: </div>
          <div className="detail-value">{data.location_group || "None"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Evidence Count: </div>
          <div className="detail-value">{data.evidence_count || 0}</div>
        </div>
      </div>
    );
  }
)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: fit-content;
  max-width: 300px;

  .detail-title {
    font-weight: bold;
    font-size: 15px;
    margin-bottom: 8px;
  }

  .detail-item {
    display: flex;
    gap: 2px;
  }

  .detail-label {
    font-weight: bold;
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 12px;
  }

  .detail-value {
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: 12px;
  }
`;

const LocationOptionComponent: React.FC<{ location: Location }> = ({
  location,
}) => {
  const theme = useTheme();

  const locationPath = location.location_group
    ? `${location.office_name}/${location.location_group}`
    : location.office_name;

  return (
    <div style={{ padding: "5px 0" }}>
      <div>{location.location_name}</div>
      <div
        style={{
          fontSize: "0.9em",
          marginTop: "4px",
          color: theme.palette.text.secondary,
          lineHeight: "1.2",
          display: "-webkit-box",
          WebkitLineClamp: 1,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {locationPath}
      </div>
    </div>
  );
};

export const LocationSelector: React.FC<LocationSelectorProps> = (props) => {
  const { query: defaultQuery } = props;

  const query: GetLocationsQuery = {
    include_groups: false,
    ...defaultQuery,
  };

  const { data, isLoading } = LocationsQuery.useGetLocations(query);

  const records: Location[] = data || [];

  // Combine number and name in label for client-side search by either
  const options: Option[] = records.map((location) => {
    const locationPath = location.location_group
      ? `${location.office_name}/${location.location_group}`
      : location.office_name;

    // Include both location name and path for searchability
    const searchableLabel = `${location.location_name} (${locationPath})`;

    return {
      label: searchableLabel,
      value: location.location_id,
      data: location,
    };
  });

  return (
    <SelectBox
      placeholder="Select or search for a location..."
      label="Location"
      OptionTooltip={TooltipContent}
      openOnFocus={false}
      {...props}
      data={options}
      searchable={true}
      loading={isLoading}
      renderOption={(item) => {
        if (typeof item === "string") {
          return <div>{item}</div>;
        }
        return <LocationOptionComponent location={item.data} />;
      }}
    />
  );
};
